import { Injectable } from '@angular/core';

import {
    DrawingRuleType,
    LayerType,
    LayerTypeEnum,
    PcvizLayer,
    PcvizLayerGroup,
    PcvizManipulator,
    PcvizProject,
    PcvizPropertySet,
    PcvizSceneView,
    PcvizWorkspaceCategories,
    PROJECT_VIEWER_TYPES,
    ProjectType,
    TooltipIcon,
    Vec,
} from '@mirage-projects/cw-models';

/**
 * Service that provides several project utility functions
 */ @Injectable({ providedIn: 'root' })
export class ProjectHelperService {
    /**
     * Gets the icon and the tooltip of a project by its type
     * @param projectType type of the project
     * @param includedInJobSite boolean to indicate if the project is shown inside a job site
     * @returns the icon and the tooltip of the project or undefined
     */
    getIconByProjectType(projectType: ProjectType | undefined, includedInJobSite = false): TooltipIcon | undefined {
        if (!projectType) {
            return;
        }

        switch (projectType) {
            case ProjectType.SCAN:
                return { icon: 'new-scanners', tooltip: 'OBJECT_BROWSER.SCAN_PROJECT' };
            case ProjectType.COLLAGE_TYPE:
                return includedInJobSite ? { icon: 'excavator', tooltip: 'JOB_SITE.BROWSER.GENERIC_PROJECT' } : undefined;
            default:
                return;
        }
    }

    /**
     * Changes the drawing rule for all open point cloud layers in the project
     * @param project The project containing the layers to update
     * @param drawingRule The drawing rule to apply
     */
    changeDrawingRule(project: PcvizProject, drawingRule: DrawingRuleType): void {
        const layers = project.getAllLayers();
        layers.forEach((layer: PcvizLayer) => {
            if (!layer.isOpen() || layer.getType() !== LayerTypeEnum.pointCloud) {
                return;
            }
            layer.initializePropertySet(project);
            layer.setProperty('drawingRule', drawingRule);
        });
    }

    /**
     * Sets a property value for all open point cloud layers in the project
     * @param project The project containing the layers
     * @param property The name of the property to set
     * @param value The value to assign to the property
     */
    setValueToMultiple(project: PcvizProject, property: string, value: string | number | boolean | Vec): void {
        const layersPointcloud = this.getAllPointCloudLayersOpened(project);
        layersPointcloud.forEach((layer) => {
            layer.setProperty(property, value);
        });
    }

    /**
     * Gets the common value of a property from all open point cloud layers
     * @param project The project containing the layers
     * @param property The property to retrieve
     */
    getValueFromMultiple(project: PcvizProject, property: string): number | string | boolean | null {
        const layersPointCloud = this.getAllPointCloudLayersOpened(project);
        if (!layersPointCloud?.length) {
            return null;
        }
        const value = layersPointCloud[0].getProperty(property);
        const allLayerHasValue = layersPointCloud.every((layer) => layer.getProperty(property) === value);
        return allLayerHasValue ? value : null;
    }

    /**
     * Gets the range of possible values for a property across all open point cloud layers
     * @param project The project containing the layers
     * @param property The property to get values from
     * @param propertySet The property set to use
     */
    getValueRangeFromMultiple(project: PcvizProject, property: string, propertySet: PcvizPropertySet): Array<string | number> {
        const layersPointCloud = this.getAllPointCloudLayersOpened(project);
        return propertySet.getValueRangeFromMultipleSelection(property, layersPointCloud);
    }

    /**
     * Initializes the property set for all open point cloud layers in the project
     * @param project The project containing the layers
     */
    initializePropertySet(project: PcvizProject): void {
        const layersPointCloud = this.getAllPointCloudLayersOpened(project);
        layersPointCloud.forEach((layer) => {
            layer.initializePropertySet(project);
        });
    }

    /**
     * Gets all open point cloud layers from the project
     * @param project The project containing the layers
     */
    getAllPointCloudLayersOpened(project: PcvizProject): PcvizLayer[] {
        return this.getAllLayersOpenedByType(project, LayerTypeEnum.pointCloud);
    }

    /**
     * Gets all open raster layers from the project
     * @param project The project containing the layers
     */
    getAllRasterLayersOpened(project: PcvizProject): PcvizLayer[] {
        return this.getAllLayersOpenedByType(project, LayerTypeEnum.rasterLayer);
    }

    /**
     * Gets all open layers of a specified type from the project
     * @param project The project containing the layers
     * @param layerType The type of layers to retrieve
     */
    // TODO: this try-catch will be erased when COLW-10083 is done
    getAllLayersOpenedByType(project: PcvizProject, layerType: LayerType): PcvizLayer[] {
        const layers = project.getAllLayers();
        const layersFiltered = layers.filter((layer) => {
            try {
                return layer.isOpen?.() && layer.getType?.() === layerType;
            } catch {
                return false;
            }
        });
        return layersFiltered;
    }

    /**
     * Sets the manipulator to work with
     * @param project Current project
     * @param view3d Current view 3d
     * @param value Sets cad manipulator if true, panorama if false
     * @param manipulator Manipulator object
     */
    setPresentVR(project: PcvizProject, view3d: PcvizSceneView, value: boolean, manipulator: PcvizManipulator): void {
        if (!view3d) {
            return;
        }

        if (value) {
            view3d.setManipulatorMode(manipulator.CAD_MANIPULATOR, {});
            // restore EDL
            const edlOn = project.getCurrentRenderSettings().get('edlOn');
            view3d.getViewer().setEDLRendering(edlOn);
        } else {
            view3d.setManipulatorMode(manipulator.PANORAMA_MANIPULATOR);
        }
    }

    /**
     * Hide layers for each category
     * @param layerGroup ToC layers
     * @param layerCategories Categories to update
     * @param workspaceCategories The PcvizWorkspaceCategories object
     * @param visibilityController visibilityController object
     */
    hideLayersByCategory(
        layerGroup: PcvizLayerGroup,
        layerCategories: any,
        workspaceCategories: PcvizWorkspaceCategories,
        visibilityController: any
    ): void {
        layerGroup.getChildren().forEach((layer: PcvizLayerGroup | PcvizLayer) => {
            const currentLayerGroup = layer as PcvizLayerGroup;
            const category = currentLayerGroup.getCategory?.();
            const isValidCategory = layerCategories.includes(category);
            const hasChildren = currentLayerGroup.getChildren?.().length;
            if (!isValidCategory && !hasChildren) {
                return;
            }
            const isStation = isValidCategory && category === workspaceCategories.getCategoryProps(workspaceCategories.Station).name;
            const isOtherValid = !isStation && isValidCategory;

            if (isStation && !currentLayerGroup.isSymbolHidden()) {
                visibilityController.setPreviousStationVisibility(currentLayerGroup.getId(), currentLayerGroup.isVisible());
                currentLayerGroup.setSymbolVisibility(false);
            } else if (isOtherValid && currentLayerGroup.isVisible()) {
                visibilityController.setPreviousLayerVisibility(currentLayerGroup.getId(), currentLayerGroup.isVisible());
                currentLayerGroup.setVisible(false, { changeToc: true });
            }

            if (hasChildren) {
                this.hideLayersByCategory(currentLayerGroup, layerCategories, workspaceCategories, visibilityController);
            }
        });
    }

    /**
     * Opens the project viewer for the given project
     */
    openProjectViewer(projectId: number, projectType: ProjectType): void {
        const relativeUrlProjectViewer = `${PROJECT_VIEWER_TYPES.get(projectType)}/${projectId}`;
        const fullUrl = `${location.protocol}//${location.host}${relativeUrlProjectViewer}`;
        location.href = fullUrl;
    }
}
